import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import type { ActivityType, AnyEntry, UserData } from '../../types'
import { FORM_FIELDS, PREFILL_FIELDS, type FormField } from '../../config/activityForms'
import { TYPE_META, uniqueEntriesByName } from '../../utils/entries'
import {
  calculateCalories,
  estimateWeightsCalories,
  timeToSeconds,
  nowDatetime,
  DATETIME_FORMAT,
} from '../../utils/calc'
import Modal from '../layout/Modal'

export type FormMode = 'new' | 'edit' | 'copy'

interface EntryFormProps {
  type: ActivityType
  mode: FormMode
  index: number | null
  seed: AnyEntry | null
  isOpen: boolean
  data: UserData
  onClose: () => void
  onSave: (type: ActivityType, entry: AnyEntry, index: number | null) => void
}

type Values = Record<string, string>

const toLocalInput = (s: string): string =>
  s ? moment(s, DATETIME_FORMAT).format('YYYY-MM-DDTHH:mm') : ''
const fromLocalInput = (s: string): string =>
  s ? moment(s, 'YYYY-MM-DDTHH:mm').format(DATETIME_FORMAT) : ''

function buildInitial(type: ActivityType, mode: FormMode, seed: AnyEntry | null): Values {
  const values: Values = {}
  for (const f of FORM_FIELDS[type]) values[f.key] = ''
  if (seed) {
    for (const f of FORM_FIELDS[type]) {
      const v = (seed as unknown as Record<string, unknown>)[f.key]
      if (v !== undefined && v !== null) values[f.key] = String(v)
    }
  }
  // New + copy always use "now"; edit keeps the stored datetime.
  if (mode !== 'edit') values.datetime = nowDatetime()
  return values
}

export default function EntryForm({
  type,
  mode,
  index,
  seed,
  isOpen,
  data,
  onClose,
  onSave,
}: EntryFormProps) {
  const [values, setValues] = useState<Values>(() => buildInitial(type, mode, seed))
  const fields = FORM_FIELDS[type]
  const nameField = TYPE_META[type].nameField

  // Re-seed whenever the form opens or its target changes.
  useEffect(() => {
    if (isOpen) setValues(buildInitial(type, mode, seed))
  }, [isOpen, type, mode, seed])

  const historyEntries = useMemo(() => uniqueEntriesByName(data, type), [data, type])

  const setField = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }))

  // Choosing a known activity name prefills its last-used settings.
  const handleNameChange = (value: string) => {
    setValues((prev) => {
      const next = { ...prev, [nameField]: value }
      const match = historyEntries.find(
        (e) => (e as unknown as Record<string, string>)[nameField] === value,
      )
      if (match) {
        for (const key of PREFILL_FIELDS) {
          const mv = (match as unknown as Record<string, unknown>)[key]
          if (mv !== undefined && mv !== null && mv !== '') next[key] = String(mv)
        }
      }
      return next
    })
  }

  // Exercise + Weights: auto-calculate calories. Weights derive the MET tier from the load
  // lifted and estimate set time from reps (~3s/rep) when no duration is given. The just-changed
  // field is passed in `over`; everything else reads from the current values.
  const recalcCalories = (over: { intensity?: string; duration?: string; reps?: string; kg?: string } = {}) => {
    const intensity = over.intensity ?? values.intensity ?? ''
    const duration = over.duration ?? values.duration ?? ''
    if (type === 'weights') {
      const reps = over.reps ?? values.repetitions ?? ''
      const kg = over.kg ?? values.kg ?? ''
      const kcal = estimateWeightsCalories(intensity, duration, reps, data.weight, kg)
      if (kcal !== null) setField('calories', String(kcal))
      return
    }
    if (type !== 'exercise') return
    const met = Number(intensity)
    const mins = duration ? timeToSeconds(duration) / 60 : 0
    if (met > 0 && mins > 0 && data.weight > 0) {
      const kcal = calculateCalories(mins, met, data.weight)
      setField('calories', String(Math.round(kcal)))
    }
  }

  const title = `${mode === 'edit' ? 'Edit' : mode === 'copy' ? 'Copy' : 'Log'} ${TYPE_META[type].label}`

  const handleSubmit = () => {
    const entry: Record<string, string> = {}
    for (const f of fields) entry[f.key] = values[f.key] ?? ''
    onSave(type, entry as unknown as AnyEntry, mode === 'edit' ? index : null)
  }

  const renderField = (f: FormField) => {
    const id = `field-${type}-${f.key}`
    const common = 'w-full'

    if (f.type === 'textarea') {
      return (
        <textarea
          id={id}
          className={common}
          rows={2}
          value={values[f.key] ?? ''}
          onChange={(e) => setField(f.key, e.target.value)}
        />
      )
    }
    if (f.type === 'select') {
      return (
        <select
          id={id}
          className={common}
          value={values[f.key] ?? ''}
          onChange={(e) => {
            setField(f.key, e.target.value)
            recalcCalories({ intensity: e.target.value })
          }}
        >
          <option value="">Select one…</option>
          {f.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )
    }
    if (f.type === 'datetime') {
      return (
        <input
          id={id}
          type="datetime-local"
          className={common}
          value={toLocalInput(values.datetime ?? '')}
          onChange={(e) => setField('datetime', fromLocalInput(e.target.value))}
        />
      )
    }
    if (f.type === 'autocomplete') {
      const listId = `${id}-list`
      return (
        <>
          <input
            id={id}
            className={common}
            list={listId}
            autoComplete="off"
            value={values[f.key] ?? ''}
            onChange={(e) => handleNameChange(e.target.value)}
          />
          <datalist id={listId}>
            {historyEntries.map((e, i) => (
              <option key={i} value={(e as unknown as Record<string, string>)[nameField]} />
            ))}
          </datalist>
        </>
      )
    }
    // text / number
    return (
      <input
        id={id}
        type={f.type === 'number' ? 'number' : 'text'}
        inputMode={f.type === 'number' ? 'decimal' : undefined}
        className={common}
        placeholder={f.placeholder}
        value={values[f.key] ?? ''}
        onChange={(e) => {
          setField(f.key, e.target.value)
          if (f.key === 'duration') recalcCalories({ duration: e.target.value })
          if (f.key === 'repetitions') recalcCalories({ reps: e.target.value })
          if (f.key === 'kg') recalcCalories({ kg: e.target.value })
        }}
      />
    )
  }

  // Group consecutive half-width fields into rows.
  const rows: FormField[][] = []
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i]
    const next = fields[i + 1]
    if (f.half && next?.half) {
      rows.push([f, next])
      i++
    } else {
      rows.push([f])
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {rows.map((row, ri) => (
          <div key={ri} className={row.length === 2 ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : ''}>
            {row.map((f) => (
              <div key={f.key}>
                <label htmlFor={`field-${type}-${f.key}`} className="block text-sm mb-1 text-[var(--text-secondary)]">
                  {f.label}
                </label>
                {renderField(f)}
                {f.help && <p className="text-xs text-[var(--text-secondary)] mt-1">{f.help}</p>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </Modal>
  )
}
